import { InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Divider, Flex, Popover, Select, Space, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useDispatch } from 'umi';

import ActionTag from '@/components/ActionTag';
import AgentName from '@/components/Agent/AgentName';
import { LeadStatePresentation, LeadStateTagPresentation } from '@/components/Filters/LeadStateFilters';
import PrettyDate, { FormatDate } from '@/components/PrettyDate';
import { LeadModel } from '@/typings/models/Lead';
import { F } from '@/utils/intl';
import { getLeadTitle } from '@/utils/lead';

import styles from './index.less';

interface RelatedLeadsProps {
  linkedLeads: LeadModel[];
  className?: string;
  currentLead?: LeadModel;
}

interface LeadOptionCardProps {
  lead: LeadModel;
  className?: string;
}

export const LeadOptionCard: React.FC<LeadOptionCardProps> = ({ lead, className }) => {
  const title = getLeadTitle(lead);
  return (
    <Flex
      className={classNames(className, styles.relatedLeadOptionContainer)}
      justify="space-between"
      align="center"
      gap={12}
      style={{ width: '100%' }}
      wrap
    >
      <Flex vertical gap={4} style={{ flex: 1 }}>
        {title && <div style={{ fontWeight: 500 }}>{title}</div>}
        <Space gap={2} wrap className={styles.relatedLeadsSecondLine}>
          <FormatDate date={lead.created} format={'YYYY-MM-DD'} />
          {lead.assignee && <AgentName hideAvatar agent={lead.assignee} />}
        </Space>
      </Flex>
      <LeadStateTagPresentation state={lead.lead_state} />
    </Flex>
  );
};

export const EachRelatedLead = ({
  lead,
  className,
  action = true,
  onClick,
  hideAppointment,
}) => {
  const dispatch = useDispatch();
  if (!lead) return null;
  const Wrapper = action ? ActionTag : React.Fragment;
  const title = getLeadTitle(lead);
  const openLeadDetails = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({
      type: 'leads/toggleLeadDetailsDrawer',
      payload: {
        lead,
      },
    });
  };
  return (
    <Wrapper
      className={classNames(className, styles.linkedLeadTag)}
      size="large"
      onClick={() => onClick(lead)}
    >
      <Space size={10} className={styles.leadContent}>
        {title && (
          <>
            {title}
            <Divider type="vertical" style={{ margin: 0 }} />
          </>
        )}
        <LeadStatePresentation state={lead.lead_state} />
        <Divider type="vertical" style={{ margin: 0 }} />
        <small>
          <PrettyDate date={lead.created} />
        </small>
        {!hideAppointment && lead?.latest_appointment && (
          <>
            <Divider type="vertical" style={{ margin: 0 }} />
            <span>
              {lead?.latest_appointment?.branch_service?.service_type?.name ||
                lead?.latest_appointment?.branch_service?.name ||
                ' - '}
            </span>
          </>
        )}
        <Tooltip title={<F id={'common.details'} defaultMessage={'Details'} />}>
          <Button
            className={styles.openLeadDetailsAction}
            size={'small'}
            color={'lime'}
            variant={'filled'}
            icon={<InfoCircleOutlined />}
            onClick={openLeadDetails}
          />
        </Tooltip>
      </Space>
    </Wrapper>
  );
};

export const LinkedLeadsList = ({
  leads,
  className,
}: {
  leads: LeadModel[];
  className: string;
}) => {
  const dispatch = useDispatch();
  const handleLeadClick = (lead: LeadModel) => {
    dispatch({
      type: 'leads/toggleLeadDetailsDrawer',
      payload: {
        lead,
        activeTab: 'serviceRequests',
      },
    });
  };

  return (
    <Flex gap={10} vertical>
      {leads.filter(Boolean).map((lead) => (
        <EachRelatedLead lead={lead} onClick={handleLeadClick} key={lead.id} />
      ))}
    </Flex>
  );
};

export const RelatedLeadsDropdown = ({
  linkedLeads,
  className,
  currentLead,
}: RelatedLeadsProps) => {
  const dispatch = useDispatch();

  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(
    linkedLeads?.[0]?.id,
  );

  if (!linkedLeads || linkedLeads.length === 0) {
    return null;
  }

  const handleLeadChange = (v: { value: string }) => {
    const leadId = v?.value; // labelInValue
    setSelectedLeadId(leadId);
    const lead = linkedLeads.find((l) => l.id === leadId);
    if (lead) {
      dispatch({
        type: 'leads/toggleLeadDetailsDrawer',
        payload: {
          lead,
          activeTab: 'serviceRequests',
        },
      });
    }
  };

  const countLabel = (
    <F
      id={`pages.lead.linkedLead${linkedLeads.length > 1 ? 's' : ''}.count`}
      defaultMessage="{count} Other Leads"
      values={{ count: linkedLeads.length }}
    />
  );

  return (
    <Select
      style={{ minWidth: 300 }}
      popupMatchSelectWidth={false}
      className={classNames(className, styles.relatedLeadsSelect)}
      popupClassName={styles.relatedLeadsDropdown}
      labelInValue
      value={{ label: countLabel }}
      onChange={handleLeadChange}
      dropdownRender={(menu) => (
        <>
          {currentLead && (
            <>
              <div className={styles.relatedLeadsDropdownHeader}>
                <LeadOptionCard lead={currentLead} />
              </div>
            </>
          )}
          <div className={styles.relatedLeadsDropdownMenu}>
            {menu}
          </div>
        </>
      )}
    >
      {linkedLeads.map((lead, index) => (
        <Select.Option key={lead.id} value={lead.id}>
          <LeadOptionCard lead={lead} />
        </Select.Option>
      ))}
    </Select>
  );
};

export default function RelatedLeads({
  linkedLeads,
  className,
}: RelatedLeadsProps) {
  if (!linkedLeads || linkedLeads.length === 0) {
    return null;
  }

  return (
    <Popover
      placement="bottomLeft"
      content={<LinkedLeadsList leads={linkedLeads} className={className} />}
    >
      <Button type="text" style={{ color: '#fff' }} size="small">
        <Space>
          <F
            id={`pages.lead.linkedLead${
              linkedLeads.length > 1 ? 's' : ''
            }.count`}
            defaultMessage="{count} Other Leads"
            values={{ count: linkedLeads.length }}
          />
          <RightOutlined style={{ fontSize: 12 }} />
        </Space>
      </Button>
    </Popover>
  );
}
